import { ClipboardDocumentIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/components/Toast';
import { canOpenInMaps, googleMapsUrl, type EventLocationGeo } from '@/lib/eventLocation';

function CompassIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M14.5 9.5 10 10.5 9 14.5 13.5 13.5 14.5 9.5Z" strokeLinejoin="round" />
    </svg>
  );
}

export default function EventLocationRow({
  event,
  className = '',
}: {
  event: EventLocationGeo & { location?: string | null };
  className?: string;
}) {
  const { showToast } = useToast();
  const location = event.location?.trim();
  if (!location) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(location);
      showToast('Location copied');
    } catch {
      showToast('Could not copy location', 'error');
    }
  };

  const openMaps = () => {
    window.open(googleMapsUrl(event), '_blank', 'noopener,noreferrer');
  };

  return (
    <span className={`inline-flex min-h-11 items-center gap-1.5 ${className}`}>
      <MapPinIcon className="h-4 w-4 shrink-0" />
      <button
        type="button"
        onClick={copy}
        className="inline-flex min-h-11 items-center gap-1.5 text-left"
        aria-label="Copy location"
      >
        <span>{location}</span>
        <ClipboardDocumentIcon className="h-4 w-4 shrink-0" />
      </button>
      {canOpenInMaps(event) && (
        <button
          type="button"
          onClick={openMaps}
          className="inline-flex h-11 w-11 items-center justify-center"
          aria-label="Open in Maps"
        >
          <CompassIcon className="h-5 w-5" />
        </button>
      )}
    </span>
  );
}
